import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "../data/services";
import { useI18n } from "../i18n/I18nProvider";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";
import { getSiteOrigin } from "../siteOrigin";
import CtaBand from "../sections/CtaBand";

export default function ServicesPage() {
  const { t, lang } = useI18n();

  const SITE = getSiteOrigin();
  const schema = [{
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/services/${s.slug}`,
      name: s.title[lang],
    })),
  }, {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "fr" ? "Accueil" : "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
    ],
  }];

  return (
    <main className="bg-[#050505] min-h-screen" data-testid="page-services">
      <SEO
        title={lang === "fr"
          ? "Services Production Vidéo & Photo Moncton NB — Bilingue FR/EN"
          : "Video & Photo Production Services Moncton NB — Bilingual FR/EN"}
        description={lang === "fr"
          ? "Production vidéo et photographique cinématique à Moncton, NB. Films de marque, drone RPAS certifié, contenu social, photographie éditoriale, direction artistique. Studio bilingue FR/EN, devis sous 24h."
          : "Cinematic video and photo production in Moncton, NB. Brand films, Transport Canada certified drone, social content, editorial photography, art direction. Bilingual FR/EN studio, 24-hour quotes."}
        path="/services"
        lang={lang}
        schema={schema}
      />

      <section className="pt-40 lg:pt-48 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
              <span className="block w-8 h-px bg-rouge" /><span>{t.services.eyebrow}</span>
            </div>
            <h1 className="mt-6 font-serif-editorial text-4xl sm:text-5xl lg:text-7xl text-ink-100 tracking-tighter leading-[1.02] max-w-5xl">
              {t.services.title}
            </h1>
            <p className="mt-6 text-ink-300 text-base lg:text-lg max-w-2xl">
              {lang === "fr"
                ? "Cinq disciplines complémentaires, un seul studio. Tout — direction, tournage, montage, étalonnage — est fait à l'interne à Moncton."
                : "Five complementary disciplines, one studio. Everything — direction, shooting, editing, grading — is done in-house in Moncton."}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-hair">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-16 lg:py-24 border-b border-hair"
              data-testid={`services-row-${s.slug}`}
            >
              <Link
                to={`/services/${s.slug}`}
                data-testid={`services-link-${s.slug}`}
                data-cursor={s.title[lang]}
                className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""} group relative overflow-hidden aspect-[4/3]`}
              >
                <img src={s.cover} alt={s.title[lang]} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover saturate-50 group-hover:saturate-100 group-hover:scale-105 transition-all duration-[1100ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase text-ink-100 bg-black/50 px-2 py-1 backdrop-blur">{s.tag}</div>
              </Link>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""} flex flex-col justify-center`}>
                <div className="text-[11px] tracking-[0.28em] uppercase text-rouge mb-3">{s.tag}</div>
                <h2 className="font-serif-editorial text-3xl sm:text-4xl lg:text-5xl text-ink-100 leading-tight">{s.title[lang]}</h2>
                <p className="mt-4 text-ink-300 text-base lg:text-lg max-w-xl leading-relaxed">{s.subtitle[lang]}</p>
                <ul className="mt-6 flex flex-wrap gap-2">
                  {s.use_cases[lang].slice(0, 3).map((u) => (
                    <li key={u} className="text-[11px] tracking-wide text-ink-300 border border-hair px-3 py-1.5">{u}</li>
                  ))}
                </ul>
                <Link
                  to={`/services/${s.slug}`}
                  className="mt-8 group inline-flex items-center gap-3 self-start text-[11px] tracking-[0.24em] uppercase text-ink-100 border-b border-ink-500 hover:border-rouge pb-2 transition-colors"
                  data-testid={`services-detail-${s.slug}`}
                >
                  {lang === "fr" ? "Découvrir" : "Discover"}
                  <ArrowRight size={14} className="text-rouge transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
